/**
 * PlacementsSection — campaign × channel placements inside the campaign
 * detail page. Deploy SAFETY mirrors the API: channel campaigns are
 * created PAUSED; going live is always an explicit Resume.
 *
 * Targeting (country, language, max CPC, negatives) is set here at creation
 * because it is what the deploy sends to the ad channel: no location means
 * worldwide serving and no CPC cap means uncapped bids, so the form defaults
 * the former to the US and requires the latter.
 *
 * The workspace daily ad limit bounds every placement: the form shows the
 * headroom left (and, at a $0 limit, that no ads can run), and a refused
 * create / deploy / resume names the limit and links to Settings, the only
 * place a person can change it.
 *
 * The max CPC is checked against the market: the form's "Check market bids"
 * shows the keyword planner's top-of-page bid range per keyword, each
 * placement shows the server's verdict for its current cap, and going live
 * with a cap below EVERY keyword's low bid asks for an explicit "go live
 * anyway" (the server refuses BID_BELOW_MARKET until it gets one).
 *
 * Serving health is the ad channel's own word, recorded by the metrics sweep:
 * Google's status, and a "not showing" / "blocked" badge with the reason —
 * including a daily budget someone changed in the ad account.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiError,
  type AdoptEditPlacementRequest,
  type AdoptKeywordBidEstimateDto,
  type AdoptPlacementDto,
  type AdoptServingState,
} from '@/lib/api';
import {
  ARCHE_PATH,
  MAX_CPC_CENTS_MAX,
  MAX_CPC_CENTS_MIN,
  PLACEMENT_COUNTRIES,
  PLACEMENT_LANGUAGES,
  ZERO_DAILY_LIMIT_MESSAGE,
} from '../constants';
import {
  useAdoptAccounts,
  useAdoptDailyAdLimit,
  useAdoptPlacementLifecycle,
  useAdoptPlacements,
  useAdoptSpendCaps,
  useCreateAdoptPlacement,
  useDeployAdoptPlacement,
  useEditAdoptPlacement,
  useAdoptKeywordBidEstimates,
} from '../hooks/useAdvertisingOptimization';
import { zeroSpendLimitName, zeroSpendLimitNote } from '../spend-limit-zero';

interface Props {
  holderId: string;
  campaignId: string;
  dailyBudgetCeilingCents: number;
  /** The parent campaign's status and kill switch decide whether Resume is possible. */
  campaignStatus: string;
  killSwitch: boolean;
}

/** Pauses the user cannot lift from here, and what lifts them. */
const NON_RESUMABLE_PAUSES: Record<string, string> = {
  kill_switch: 'Paused by the kill switch.',
  admin: 'Paused by a platform emergency stop. It becomes resumable when the stop is lifted.',
  budget_cap: 'Paused because the campaign reached its total budget. Raise the budget first.',
  policy: 'Paused because the campaign ended.',
  daily_limit_reached:
    "Paused until tomorrow — today's spend reached the workspace daily ad limit. It resumes " +
    "automatically on the next day in the ad account's time zone.",
  wallet_daily_cap_reached:
    "Paused until tomorrow — today's managed-account spend reached the wallet's daily cap. It " +
    "resumes automatically on the next day in the ad account's time zone.",
};

/** Resumable pauses that still deserve a word of explanation. */
const RESUMABLE_PAUSE_NOTES: Record<string, string> = {
  daily_limit_lowered:
    'Paused because the workspace daily ad limit was lowered below what was running (or to ' +
    '$0). It does not resume by itself — resume it if it fits the new limit.',
  spend_cap_zero:
    'Paused because a workspace spend cap was set to $0. It does not resume by itself — ' +
    'resume it once a workspace owner or admin raises or removes the cap.',
  monthly_cap_reached:
    "Paused until next month — this month's ad spend reached the workspace monthly cap. It " +
    'resumes automatically next month; Resume works sooner only once a workspace owner or ' +
    'admin raises or removes the cap.',
  wallet_monthly_cap_reached:
    "Paused until next month — this month's managed-account spend reached the wallet's " +
    'monthly cap. It resumes automatically next month; Resume works sooner only once a ' +
    'workspace owner or admin raises or removes the cap.',
};

/** Refusals that come from a spend limit — only a person can change those, in Settings. */
const SPEND_LIMIT_CODES = new Set([
  'DAILY_AD_LIMIT_REQUIRED',
  'DAILY_AD_LIMIT_EXCEEDED',
  'DAILY_AD_LIMIT_ZERO',
  'MONTHLY_AD_CAP_REACHED',
  'WALLET_DAILY_CAP_REACHED',
  'WALLET_MONTHLY_CAP_REACHED',
  'SPEND_READING_STALE',
]);

/**
 * A refused action's message. A spend-limit refusal also links to Settings —
 * the only place a person can set or change a limit.
 */
function ActionError({ error, fallback }: { error: unknown; fallback: string }) {
  const message = error instanceof Error ? error.message : fallback;
  const limitRefusal = error instanceof ApiError && SPEND_LIMIT_CODES.has(error.code ?? '');
  return (
    <div className="text-xs text-rose-600" role="alert">
      {message}
      {limitRefusal && (
        <>
          {' '}
          <Link to={`${ARCHE_PATH}/settings`} className="underline">
            Open spend limit settings
          </Link>
        </>
      )}
    </div>
  );
}

/**
 * Why Resume would be refused, or null when it is allowed. Mirrors the
 * resume route's checks so the page explains the block instead of offering a
 * button that 409s.
 */
function resumeBlockedReason(
  placement: AdoptPlacementDto,
  campaignStatus: string,
  killSwitch: boolean,
  /** Set when a $0 spend limit keeps it paused (it then never resumes by itself). */
  zeroNote: string | null,
): string | null {
  if (killSwitch) return 'The campaign kill switch is engaged. Release it before resuming.';
  if (placement.pauseReason && NON_RESUMABLE_PAUSES[placement.pauseReason]) {
    return zeroNote ?? NON_RESUMABLE_PAUSES[placement.pauseReason];
  }
  if (campaignStatus !== 'active') {
    return `The campaign is ${campaignStatus}. Activate it before resuming this placement.`;
  }
  return null;
}

function centsToUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/** Bids read as money to the cent: "$0.10", not "$0.1". */
function cpcToUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * How a typed max CPC compares with the keyword planner's low top-of-page
 * bids — the preview's counterpart of the server verdict, recomputed as the
 * person types.
 */
function previewBidVerdict(
  rows: AdoptKeywordBidEstimateDto[],
  maxCpcCents: number | null,
  dailyBudgetCents: number,
): {
  withData: number;
  below: number;
  minLow: number;
  maxLow: number;
  suggestedCents: number;
  roughClicksPerDay: number | null;
} | null {
  const lows = rows
    .map((r) => r.lowTopOfPageBidCents)
    .filter((c): c is number => c !== null && c > 0)
    .sort((a, b) => a - b);
  if (lows.length === 0) return null;
  const mid = Math.floor(lows.length / 2);
  const median = lows.length % 2 === 1 ? lows[mid] : (lows[mid - 1] + lows[mid]) / 2;
  return {
    withData: lows.length,
    below: maxCpcCents === null ? 0 : lows.filter((low) => maxCpcCents < low).length,
    minLow: lows[0],
    maxLow: lows[lows.length - 1],
    // The server's rule: a whole-dollar cap over every low bid, never above the budget.
    suggestedCents: Math.min(
      Math.ceil(lows[lows.length - 1] / 100) * 100,
      MAX_CPC_CENTS_MAX,
      Number.isFinite(dailyBudgetCents) && dailyBudgetCents > 0
        ? dailyBudgetCents
        : Number.POSITIVE_INFINITY,
    ),
    roughClicksPerDay:
      Number.isFinite(dailyBudgetCents) && dailyBudgetCents > 0
        ? Math.floor(dailyBudgetCents / median)
        : null,
  };
}

function MarketBidCheck({
  holderId,
  channelAccountId,
  keywords,
  country,
  language,
  maxCpcCents,
  dailyBudgetCents,
}: {
  holderId: string;
  channelAccountId: string;
  keywords: string[];
  country: string;
  language: string;
  maxCpcCents: number | null;
  dailyBudgetCents: number;
}) {
  const estimate = useAdoptKeywordBidEstimates(holderId);
  // A result is about the keywords and market it was asked for: changing
  // either clears it rather than grading the new cap against old data.
  const marketKey = JSON.stringify([keywords, country, language]);
  const { reset } = estimate;
  useEffect(() => {
    reset();
  }, [marketKey, reset]);
  const check = estimate.data?.bidCheck;
  const verdict =
    check && !check.error ? previewBidVerdict(check.keywords, maxCpcCents, dailyBudgetCents) : null;

  return (
    <div className="flex flex-col gap-2" data-testid="market-bid-check">
      <button
        type="button"
        onClick={() =>
          estimate.mutate({
            channelAccountId,
            keywords,
            locations: [country],
            languages: [language],
          })
        }
        disabled={!channelAccountId || keywords.length === 0 || estimate.isPending}
        className="self-start px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
      >
        {estimate.isPending ? 'Checking market bids…' : 'Check market bids'}
      </button>
      {estimate.isError && (
        <ActionError error={estimate.error} fallback="Could not check market bids" />
      )}
      {check?.error && (
        <div className="text-xs text-amber-700">Market bids are unavailable: {check.error}</div>
      )}
      {check && !check.error && (
        <>
          <table className="text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left font-normal pr-3">Keyword</th>
                <th className="text-right font-normal pr-3">Monthly searches</th>
                <th className="text-right font-normal">Top-of-page bid</th>
              </tr>
            </thead>
            <tbody>
              {check.keywords.map((k) => (
                <tr key={k.text}>
                  <td className="pr-3">{k.text}</td>
                  <td className="text-right pr-3 tabular-nums">
                    {k.avgMonthlySearches === null ? '—' : k.avgMonthlySearches.toLocaleString()}
                  </td>
                  <td className="text-right tabular-nums">
                    {k.lowTopOfPageBidCents === null
                      ? '—'
                      : `${cpcToUsd(k.lowTopOfPageBidCents)}–${cpcToUsd(k.highTopOfPageBidCents ?? k.lowTopOfPageBidCents)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {verdict === null ? (
            <p className="text-xs text-muted-foreground">
              The keyword planner has no bid data for these keywords here.
            </p>
          ) : maxCpcCents === null ? (
            <p className="text-xs text-muted-foreground">
              Low top-of-page bids run {cpcToUsd(verdict.minLow)}–{cpcToUsd(verdict.maxLow)}. About{' '}
              {cpcToUsd(verdict.suggestedCents)} would compete for every keyword.
            </p>
          ) : verdict.below === verdict.withData ? (
            <p className="text-xs text-rose-700" data-testid="bid-preview-below-all">
              {cpcToUsd(maxCpcCents)} is below the low top-of-page bid for every keyword (
              {cpcToUsd(verdict.minLow)}–{cpcToUsd(verdict.maxLow)}), so the ads would rarely or
              never show. About {cpcToUsd(verdict.suggestedCents)} would compete.
            </p>
          ) : verdict.below > 0 ? (
            <p className="text-xs text-amber-700">
              {cpcToUsd(maxCpcCents)} is below the low top-of-page bid for {verdict.below} of{' '}
              {verdict.withData} keywords; those would rarely show. About{' '}
              {cpcToUsd(verdict.suggestedCents)} would compete for all of them.
            </p>
          ) : (
            <p className="text-xs text-emerald-700">
              {cpcToUsd(maxCpcCents)} clears the low top-of-page bid for every keyword.
            </p>
          )}
          {verdict?.roughClicksPerDay !== null && verdict?.roughClicksPerDay !== undefined && (
            <p className="text-xs text-muted-foreground">
              At market prices this daily budget buys roughly {verdict.roughClicksPerDay} click
              {verdict.roughClicksPerDay === 1 ? '' : 's'} a day — a rough guide, not a forecast.
            </p>
          )}
        </>
      )}
    </div>
  );
}

const SERVING_BADGES: Partial<Record<AdoptServingState, { label: string; style: string }>> = {
  serving: { label: 'showing', style: 'bg-emerald-100 text-emerald-800' },
  starting: { label: 'starting', style: 'bg-amber-100 text-amber-800' },
  not_serving: { label: 'not showing', style: 'bg-rose-100 text-rose-800' },
  blocked: { label: 'blocked', style: 'bg-rose-100 text-rose-800' },
};

/** "12 minutes ago" / "3 hours ago" — how fresh the channel's word is. */
function ago(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
}

/**
 * What the ad channel itself reports — its status, and every problem the
 * server found (not showing, blocked, budget changed in the ad account, …).
 * The below-market bid is BidStatus's line, so it is not repeated here.
 */
function ServingHealth({
  placement,
  holderId,
  campaignId,
}: {
  placement: AdoptPlacementDto;
  holderId: string;
  campaignId: string;
}) {
  // Re-sending the recorded budget puts Google back to it (the server
  // re-applies an unchanged budget when the channel drifted).
  const reapply = useEditAdoptPlacement(holderId, campaignId);
  const health = placement.servingAssessment;
  if (!health || !placement.externalCampaignRef) return null;
  const problems = health.problems.filter((p) => p.code !== 'BID_BELOW_MARKET');
  return (
    <div className="flex flex-col gap-1" data-testid="placement-serving-health">
      {health.channelStatusLabel && (
        <div className="text-xs text-muted-foreground">
          Google: {health.channelStatusLabel}
          {health.checkedAt ? ` · checked ${ago(health.checkedAt)}` : ''}
        </div>
      )}
      {problems.map((p) => (
        <div
          key={p.code}
          className={`text-xs ${p.severity === 'critical' ? 'text-rose-700' : 'text-amber-700'}`}
          role={p.severity === 'critical' ? 'alert' : undefined}
        >
          {p.message}
          {p.code === 'BUDGET_DRIFT' && health.budgetDrift && (
            <>
              {' '}
              <button
                onClick={() =>
                  reapply.mutate({
                    placementId: placement.id,
                    body: { dailyBudgetCents: health.budgetDrift!.recordCents },
                  })
                }
                disabled={reapply.isPending}
                className="underline disabled:opacity-50"
              >
                Put Google back to {centsToUsd(health.budgetDrift.recordCents)}/day
              </button>
            </>
          )}
        </div>
      ))}
      {reapply.isError && (
        <ActionError error={reapply.error} fallback="Could not update the budget on Google" />
      )}
    </div>
  );
}

/** The server's verdict for a placement's current max CPC, as one line. */
function BidStatus({ placement }: { placement: AdoptPlacementDto }) {
  const bid = placement.bidAssessment;
  if (!bid) return null;
  // "Not showing" already names a below-market cap as its cause.
  if (placement.servingAssessment?.problems.some((p) => p.code === 'NOT_SHOWING')) return null;
  if (placement.bidWarning) {
    return (
      <div
        className={`text-xs ${bid.verdict === 'below_all' ? 'text-rose-700' : 'text-amber-700'}`}
        data-testid="placement-bid-warning"
      >
        {placement.bidWarning}
      </div>
    );
  }
  if (bid.verdict === 'competitive') {
    return (
      <div className="text-xs text-muted-foreground">
        Market check: the max CPC clears every keyword&apos;s low top-of-page bid.
      </div>
    );
  }
  if (bid.verdict === 'stale') {
    return (
      <div className="text-xs text-muted-foreground">
        The keywords or market changed since the last bid check; it refreshes within a day.
      </div>
    );
  }
  if (placement.bidCheck?.error) {
    return (
      <div className="text-xs text-muted-foreground">
        Market bid check unavailable: {placement.bidCheck.error}
      </div>
    );
  }
  return null;
}

function labelFor(options: ReadonlyArray<{ id: string; label: string }>, id: string): string {
  return options.find((o) => o.id === id)?.label ?? id;
}

/** "$1.50" → 150, or null when outside the server's bounds. */
function parseMaxCpcCents(usd: string): number | null {
  if (usd.trim() === '') return null;
  const cents = Math.round(Number(usd) * 100);
  if (!Number.isFinite(cents) || cents < MAX_CPC_CENTS_MIN || cents > MAX_CPC_CENTS_MAX) {
    return null;
  }
  return cents;
}

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  deploying: 'bg-amber-100 text-amber-800',
  paused: 'bg-amber-100 text-amber-800',
  live: 'bg-emerald-100 text-emerald-800',
  error: 'bg-rose-100 text-rose-800',
};

/** The edit form's starting values, read from the placement as it is. */
function initialFormValues(existing: AdoptPlacementDto | undefined) {
  const t = existing?.targeting ?? {};
  const positives = (t.keywords ?? []).filter((k) => !k.negative);
  return {
    dailyBudgetUsd: existing ? String(existing.dailyBudgetCents / 100) : '10',
    keywordsText: positives.map((k) => k.text).join('\n'),
    matchType: (positives[0]?.matchType ?? 'phrase') as 'exact' | 'phrase' | 'broad',
    mixedMatchTypes: new Set(positives.map((k) => k.matchType)).size > 1,
    // Negative keywords stored with their own match type are kept as they are.
    negativeKeywordObjects: (t.keywords ?? []).filter((k) => k.negative),
    negativesText: (t.negativeKeywords ?? []).join('\n'),
    country: t.locations?.[0] ?? PLACEMENT_COUNTRIES[0].id,
    language: t.languages?.[0] ?? PLACEMENT_LANGUAGES[0].id,
    maxCpcUsd: typeof t.maxCpcCents === 'number' ? String(t.maxCpcCents / 100) : '',
  };
}

function CreatePlacementForm({
  holderId,
  campaignId,
  dailyBudgetCeilingCents,
  onDone,
  existing,
}: Pick<Props, 'holderId' | 'campaignId' | 'dailyBudgetCeilingCents'> & {
  onDone: () => void;
  /** Edit this placement instead of creating one (the account stays fixed). */
  existing?: AdoptPlacementDto;
}) {
  const accountsQuery = useAdoptAccounts(holderId);
  const limitQuery = useAdoptDailyAdLimit(holderId);
  const createPlacement = useCreateAdoptPlacement(holderId, campaignId);
  const editPlacement = useEditAdoptPlacement(holderId, campaignId);
  const [initial] = useState(() => initialFormValues(existing));
  const [accountId, setAccountId] = useState('');
  const [dailyBudgetUsd, setDailyBudgetUsd] = useState(initial.dailyBudgetUsd);
  const [keywordsText, setKeywordsText] = useState(initial.keywordsText);
  const [matchType, setMatchType] = useState<'exact' | 'phrase' | 'broad'>(initial.matchType);
  const [country, setCountry] = useState<string>(initial.country);
  const [language, setLanguage] = useState<string>(initial.language);
  const [maxCpcUsd, setMaxCpcUsd] = useState(initial.maxCpcUsd);
  const [negativesText, setNegativesText] = useState(initial.negativesText);
  const mutation = existing ? editPlacement : createPlacement;

  const activeAccounts = (accountsQuery.data ?? []).filter((a) => a.status === 'active');
  const keywords = keywordsText
    .split('\n')
    .map((k) => k.trim())
    .filter(Boolean)
    .map((text) => ({ text, matchType }));
  const negativeKeywords = negativesText
    .split('\n')
    .map((k) => k.trim())
    .filter(Boolean);
  const maxCpcCents = parseMaxCpcCents(maxCpcUsd);
  const limit = limitQuery.data;
  const budgetCents = Math.round(Number(dailyBudgetUsd) * 100);

  // An edit sends only what changed — the single country / language pickers
  // must not flatten a multi-location placement nobody touched here.
  const editBody: AdoptEditPlacementRequest = {};
  if (existing) {
    if (dailyBudgetUsd !== initial.dailyBudgetUsd) editBody.dailyBudgetCents = budgetCents;
    if (keywordsText !== initial.keywordsText || matchType !== initial.matchType) {
      editBody.keywords = [...keywords, ...initial.negativeKeywordObjects];
    }
    if (negativesText !== initial.negativesText) editBody.negativeKeywords = negativeKeywords;
    if (country !== initial.country) editBody.locations = [country];
    if (language !== initial.language) editBody.languages = [language];
    if (maxCpcUsd !== initial.maxCpcUsd && maxCpcCents !== null) editBody.maxCpcCents = maxCpcCents;
  }
  const nothingChanged = existing !== undefined && Object.keys(editBody).length === 0;

  const submit = async (): Promise<void> => {
    if (maxCpcCents === null) return;
    if (existing) {
      await editPlacement.mutateAsync({ placementId: existing.id, body: editBody });
      onDone();
      return;
    }
    await createPlacement.mutateAsync({
      channelAccountId: accountId,
      dailyBudgetCents: Math.round(Number(dailyBudgetUsd) * 100),
      keywords,
      negativeKeywords,
      locations: [country],
      languages: [language],
      maxCpcCents,
    });
    onDone();
  };

  // Creating needs an account to put the placement on; an edit keeps its own.
  if (!existing && !accountsQuery.isLoading && activeAccounts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No active channel account. Connect a Google Ads account in Settings first.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {existing?.externalCampaignRef && (
        <p className="text-xs text-muted-foreground">
          Deployed: saving changes the campaign on Google straight away. A higher budget must fit
          the workspace&apos;s daily ad limit.
        </p>
      )}
      {!existing && (
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Channel account</span>
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Select account…</option>
            {activeAccounts.map((a) => (
              <option key={a.channelAccountId} value={a.channelAccountId}>
                {a.descriptiveName ?? a.externalCustomerId ?? a.channelAccountId} ({a.mode})
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">
          Daily budget (USD, ceiling {centsToUsd(dailyBudgetCeilingCents)})
        </span>
        <input
          type="number"
          min={1}
          className="w-40 rounded-md border border-border bg-background px-2 py-1.5"
          value={dailyBudgetUsd}
          onChange={(e) => setDailyBudgetUsd(e.target.value)}
        />
      </label>
      {limit && (
        <div className="text-xs text-muted-foreground" data-testid="placement-limit-headroom">
          {limit.limitCents === null ? (
            <span className="text-rose-700">
              This workspace has no daily ad limit, so no placement can be created.{' '}
              <Link to={`${ARCHE_PATH}/settings`} className="underline">
                Set one in Settings
              </Link>
              .
            </span>
          ) : limit.limitCents === 0 ? (
            <span className="text-rose-700" data-testid="placement-limit-zero">
              Workspace daily ad limit $0/day · $0/day of headroom left · {ZERO_DAILY_LIMIT_MESSAGE}{' '}
              <Link to={`${ARCHE_PATH}/settings`} className="underline">
                A workspace owner or admin can raise it in Settings
              </Link>
              .
            </span>
          ) : (
            <>
              Workspace daily ad limit {centsToUsd(limit.limitCents)}/day ·{' '}
              {centsToUsd(limit.headroomCents ?? 0)}/day of headroom left
              {Number.isFinite(budgetCents) && budgetCents > limit.limitCents ? (
                <span className="text-rose-700"> · this budget is above the limit</span>
              ) : Number.isFinite(budgetCents) && budgetCents > (limit.headroomCents ?? 0) ? (
                <span className="text-amber-700">
                  {' '}
                  · more than the headroom left: it cannot go live until other placements are paused
                  or the limit is raised
                </span>
              ) : null}
            </>
          )}
        </div>
      )}
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">Keywords (one per line)</span>
        <textarea
          rows={4}
          className="rounded-md border border-border bg-background px-2 py-1.5 font-mono text-sm"
          value={keywordsText}
          onChange={(e) => setKeywordsText(e.target.value)}
          placeholder={'free photoshop alternative\nonline photo editor'}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm w-40">
        <span className="text-muted-foreground">Match type</span>
        <select
          className="rounded-md border border-border bg-background px-2 py-1.5"
          value={matchType}
          onChange={(e) => setMatchType(e.target.value as typeof matchType)}
        >
          <option value="exact">exact</option>
          <option value="phrase">phrase</option>
          <option value="broad">broad</option>
        </select>
      </label>
      {initial.mixedMatchTypes && (
        <p className="text-xs text-amber-700">
          These keywords use more than one match type; changing them applies the selected match type
          to all of them.
        </p>
      )}
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">Negative keywords (one per line, optional)</span>
        <textarea
          rows={3}
          className="rounded-md border border-border bg-background px-2 py-1.5 font-mono text-sm"
          value={negativesText}
          onChange={(e) => setNegativesText(e.target.value)}
          placeholder={'jobs\ntutorial'}
        />
      </label>
      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Country (people in it)</span>
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            {PLACEMENT_COUNTRIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Language</span>
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {PLACEMENT_LANGUAGES.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Max CPC (USD, required)</span>
          <input
            type="number"
            min={MAX_CPC_CENTS_MIN / 100}
            max={MAX_CPC_CENTS_MAX / 100}
            step="0.01"
            className="w-32 rounded-md border border-border bg-background px-2 py-1.5"
            value={maxCpcUsd}
            onChange={(e) => setMaxCpcUsd(e.target.value)}
            placeholder="1.50"
          />
        </label>
      </div>
      {maxCpcUsd.trim() !== '' && maxCpcCents === null && (
        <div className="text-xs text-rose-600">
          Max CPC must be between {cpcToUsd(MAX_CPC_CENTS_MIN)} and {cpcToUsd(MAX_CPC_CENTS_MAX)}.
        </div>
      )}
      <MarketBidCheck
        holderId={holderId}
        channelAccountId={existing?.channelAccountId ?? accountId}
        keywords={keywords.map((k) => k.text)}
        country={country}
        language={language}
        maxCpcCents={maxCpcCents}
        dailyBudgetCents={budgetCents}
      />
      {mutation.isError && (
        <ActionError
          error={mutation.error}
          fallback={existing ? 'Failed to save the placement' : 'Failed to create placement'}
        />
      )}
      <div className="flex gap-2">
        <button
          onClick={() => void submit()}
          disabled={
            (!existing && !accountId) ||
            nothingChanged ||
            keywords.length === 0 ||
            maxCpcCents === null ||
            mutation.isPending ||
            // The server refuses these too; say so before the click.
            (limit !== undefined && (limit.limitCents === null || budgetCents > limit.limitCents))
          }
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          {existing ? 'Save changes' : 'Create placement'}
        </button>
        <button onClick={onDone} className="px-3 py-1.5 rounded-md border border-border text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

function PlacementRow({
  placement,
  holderId,
  campaignId,
  campaignStatus,
  killSwitch,
  zeroLimitName,
  dailyBudgetCeilingCents,
}: {
  placement: AdoptPlacementDto;
  holderId: string;
  campaignId: string;
  dailyBudgetCeilingCents: number;
  campaignStatus: string;
  killSwitch: boolean;
  /** The spend limit set to $0 that keeps this placement paused, if any. */
  zeroLimitName: string | null;
}) {
  const deploy = useDeployAdoptPlacement(holderId, campaignId);
  const lifecycle = useAdoptPlacementLifecycle(holderId, campaignId);
  const [editing, setEditing] = useState(false);
  const [confirmingBelowMarket, setConfirmingBelowMarket] = useState(false);
  const belowMarket = placement.bidAssessment?.verdict === 'below_all';
  // The server asks too (409), e.g. when the check landed after this page loaded.
  const refusedBelowMarket =
    lifecycle.error instanceof ApiError && lifecycle.error.code === 'BID_BELOW_MARKET';
  const resume = (acknowledgeBelowMarketBid: boolean) => {
    lifecycle.mutate(
      { placementId: placement.id, verb: 'resume', acknowledgeBelowMarketBid },
      { onSuccess: () => setConfirmingBelowMarket(false) },
    );
  };
  const zeroNote =
    placement.status === 'paused' && zeroLimitName ? zeroSpendLimitNote(zeroLimitName) : null;
  const blockedReason =
    placement.status === 'paused'
      ? resumeBlockedReason(placement, campaignStatus, killSwitch, zeroNote)
      : null;
  const pauseNote = placement.pauseReason
    ? (zeroNote ?? RESUMABLE_PAUSE_NOTES[placement.pauseReason])
    : undefined;
  const targeting = placement.targeting ?? {};
  const keywordCount = targeting.keywords?.length ?? 0;
  const negativeCount = targeting.negativeKeywords?.length ?? 0;
  const countries = (targeting.locations ?? []).map((id) => labelFor(PLACEMENT_COUNTRIES, id));
  const languages = (targeting.languages ?? []).map((id) => labelFor(PLACEMENT_LANGUAGES, id));
  const hasCpcCap = typeof targeting.maxCpcCents === 'number';

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium capitalize">{placement.channel.replace('_', ' ')}</span>
          <span
            className={`text-xs rounded px-1.5 py-0.5 ${STATUS_STYLES[placement.status] ?? 'bg-muted'}`}
          >
            {placement.status}
            {placement.pauseReason ? ` (${placement.pauseReason})` : ''}
          </span>
          {placement.servingAssessment && SERVING_BADGES[placement.servingAssessment.state] && (
            <span
              className={`text-xs rounded px-1.5 py-0.5 ${SERVING_BADGES[placement.servingAssessment.state]!.style}`}
              data-testid="placement-serving-badge"
            >
              {SERVING_BADGES[placement.servingAssessment.state]!.label}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {placement.status !== 'deploying' && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="px-2.5 py-1 rounded-md border border-border text-xs"
            >
              Edit
            </button>
          )}
          {(placement.status === 'draft' || placement.status === 'error') && (
            <button
              onClick={() => deploy.mutate(placement.id)}
              disabled={deploy.isPending || !hasCpcCap}
              className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs disabled:opacity-50"
            >
              Deploy (starts paused)
            </button>
          )}
          {placement.status === 'paused' && blockedReason === null && (
            <button
              onClick={() => (belowMarket ? setConfirmingBelowMarket(true) : resume(false))}
              disabled={lifecycle.isPending || confirmingBelowMarket}
              className="px-2.5 py-1 rounded-md bg-emerald-600 text-white text-xs disabled:opacity-50"
            >
              Resume (go live)
            </button>
          )}
          {(placement.status === 'live' || placement.pausePending) && (
            <button
              onClick={() => lifecycle.mutate({ placementId: placement.id, verb: 'pause' })}
              disabled={lifecycle.isPending}
              className="px-2.5 py-1 rounded-md border border-rose-300 text-rose-700 text-xs disabled:opacity-50"
            >
              {placement.pausePending ? 'Retry pause' : 'Pause'}
            </button>
          )}
        </div>
      </div>
      <div className="text-sm text-muted-foreground">
        {centsToUsd(placement.dailyBudgetCents)}/day · {keywordCount} keyword
        {keywordCount === 1 ? '' : 's'}
        {negativeCount > 0 ? ` · ${negativeCount} negative${negativeCount === 1 ? '' : 's'}` : ''}
        {hasCpcCap ? ` · max CPC ${cpcToUsd(targeting.maxCpcCents!)}` : ''}
        {placement.externalCampaignRef ? ` · channel ref ${placement.externalCampaignRef}` : ''}
      </div>
      {(countries.length > 0 || languages.length > 0) && (
        <div className="text-xs text-muted-foreground">
          {countries.length > 0 ? `In ${countries.join(', ')}` : ''}
          {countries.length > 0 && languages.length > 0 ? ' · ' : ''}
          {languages.join(', ')}
        </div>
      )}
      <ServingHealth placement={placement} holderId={holderId} campaignId={campaignId} />
      <BidStatus placement={placement} />
      {(confirmingBelowMarket || refusedBelowMarket) && (
        <div
          role="alertdialog"
          aria-label="Go live with a below-market bid?"
          className="rounded-md border border-rose-300 p-3 text-xs flex flex-col gap-2"
        >
          <p className="text-rose-700">
            {refusedBelowMarket && lifecycle.error instanceof Error
              ? lifecycle.error.message
              : `${placement.bidWarning ?? 'The max CPC is below the market for every keyword.'} The ads would rarely or never show.`}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setConfirmingBelowMarket(false);
                lifecycle.reset();
                setEditing(true);
              }}
              className="px-2.5 py-1 rounded-md bg-primary text-primary-foreground"
            >
              Raise the max CPC
            </button>
            <button
              onClick={() => resume(true)}
              disabled={lifecycle.isPending}
              className="px-2.5 py-1 rounded-md border border-rose-300 text-rose-700 disabled:opacity-50"
            >
              Go live anyway
            </button>
            <button
              onClick={() => {
                setConfirmingBelowMarket(false);
                lifecycle.reset();
              }}
              className="px-2.5 py-1 rounded-md border border-border"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {!hasCpcCap && (placement.status === 'draft' || placement.status === 'error') && (
        <div className="text-xs text-amber-700">
          No max CPC on this placement, so it cannot be deployed. Create a new placement with a max
          CPC.
        </div>
      )}
      {placement.lastDeployError && (
        <div className="text-xs text-rose-600">{placement.lastDeployError}</div>
      )}
      {placement.pausePending && (
        <div className="text-xs text-amber-700">
          Paused here, but Google has not confirmed it yet, so the campaign may still be serving.
          Retrying automatically.
          {placement.lastPauseError ? ` Last error: ${placement.lastPauseError}` : ''}
        </div>
      )}
      {blockedReason && <div className="text-xs text-muted-foreground">{blockedReason}</div>}
      {placement.status === 'paused' && !blockedReason && pauseNote && (
        <div className="text-xs text-muted-foreground">{pauseNote}</div>
      )}
      {(deploy.isError || (lifecycle.isError && !refusedBelowMarket)) && (
        <ActionError error={deploy.error ?? lifecycle.error} fallback="Action failed" />
      )}
      {editing && (
        <div className="mt-2 rounded-md border border-border p-3">
          <CreatePlacementForm
            holderId={holderId}
            campaignId={campaignId}
            dailyBudgetCeilingCents={dailyBudgetCeilingCents}
            existing={placement}
            onDone={() => setEditing(false)}
          />
        </div>
      )}
    </div>
  );
}

export function PlacementsSection({
  holderId,
  campaignId,
  dailyBudgetCeilingCents,
  campaignStatus,
  killSwitch,
}: Props) {
  const placementsQuery = useAdoptPlacements(holderId, campaignId);
  // Only to tell a placement kept paused by a $0 limit apart from one that
  // resumes by itself; the server decides either way.
  const limitQuery = useAdoptDailyAdLimit(holderId);
  const capsQuery = useAdoptSpendCaps(holderId);
  const [creating, setCreating] = useState(false);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">Channel placements</h2>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="text-sm text-primary hover:underline"
          >
            + New placement
          </button>
        )}
      </div>
      {creating && (
        <div className="rounded-lg border border-border p-4">
          <CreatePlacementForm
            holderId={holderId}
            campaignId={campaignId}
            dailyBudgetCeilingCents={dailyBudgetCeilingCents}
            onDone={() => setCreating(false)}
          />
        </div>
      )}
      {placementsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading placements…</p>
      ) : (placementsQuery.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No placements yet. A placement maps this campaign onto a connected ad account.
        </p>
      ) : (
        (placementsQuery.data ?? []).map((p) => (
          <PlacementRow
            key={p.id}
            placement={p}
            holderId={holderId}
            campaignId={campaignId}
            campaignStatus={campaignStatus}
            killSwitch={killSwitch}
            dailyBudgetCeilingCents={dailyBudgetCeilingCents}
            zeroLimitName={zeroSpendLimitName(p.pauseReason, limitQuery.data, capsQuery.data)}
          />
        ))
      )}
    </section>
  );
}
